import { createFileRoute, notFound } from '@tanstack/react-router'
import type { BlogPost } from '@/types'

export const Route = createFileRoute('/blog/$slug')({
  component: BlogPostPage,
  loader: async ({ params }) => {
    try {
      const response = await fetch('/data/blog-posts.json')
      const posts: BlogPost[] = await response.json()
      const post = posts.find((p) => p.slug === params.slug)
      if (!post) throw notFound()
      return { post }
    } catch {
      throw notFound()
    }
  },
  notFoundComponent: () => (
    <div className="container mx-auto px-4 py-16 text-center">
      <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
      <a href="/blog" className="text-blue-600 hover:underline">
        View all articles
      </a>
    </div>
  ),
})

function BlogPostPage() {
  const { post } = Route.useLoaderData()

  return (
    <article className="py-8">
      {/* Header */}
      <header className="max-w-3xl mx-auto px-4 mb-8">
        {post.categories && post.categories.length > 0 && (
          <div className="flex gap-2 mb-4">
            {post.categories.map((cat) => (
              <span
                key={cat.id}
                className="text-sm text-blue-600 font-medium"
              >
                {cat.name}
              </span>
            ))}
          </div>
        )}

        <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6 leading-tight">
          {post.title}
        </h1>

        <div className="flex items-center gap-4 text-gray-600">
          {post.author && (
            <div className="flex items-center gap-3">
              {post.author.avatar_url && (
                <img
                  src={post.author.avatar_url}
                  alt={post.author.name}
                  className="w-10 h-10 rounded-full"
                />
              )}
              <span className="font-medium">{post.author.name}</span>
            </div>
          )}
          {post.published_at && (
            <>
              <span className="text-gray-400">·</span>
              <time>{new Date(post.published_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}</time>
            </>
          )}
          {post.reading_time_minutes && (
            <>
              <span className="text-gray-400">·</span>
              <span>{post.reading_time_minutes} min read</span>
            </>
          )}
        </div>
      </header>

      {/* Featured Image */}
      {post.featured_image && (
        <figure className="max-w-4xl mx-auto mb-12">
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full rounded-lg"
          />
        </figure>
      )}

      {/* Content */}
      <div
        className="prose prose-lg max-w-3xl mx-auto px-4"
        dangerouslySetInnerHTML={{ __html: post.content }}
      />

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="max-w-3xl mx-auto px-4 mt-12 pt-8 border-t">
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag) => (
              <span
                key={tag.id}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
              >
                #{tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Author bio */}
      {post.author && post.author.bio && (
        <div className="max-w-3xl mx-auto px-4 mt-12 pt-8 border-t">
          <div className="flex gap-4">
            {post.author.avatar_url && (
              <img
                src={post.author.avatar_url}
                alt={post.author.name}
                className="w-16 h-16 rounded-full"
              />
            )}
            <div>
              <h3 className="font-semibold mb-1">About {post.author.name}</h3>
              <p className="text-gray-600">{post.author.bio}</p>
            </div>
          </div>
        </div>
      )}
    </article>
  )
}
